// Work profitability: revenue, costs, and profit per job for a date range.
// Reads only the confirmed job-profitability endpoint. Amounts are integer
// minor units — cents — never floats.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { getJobProfitability, type JobProfitabilityReport } from "@/lib/api";
import { ReportError } from "./StatementTables";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function janFirstIso(): string {
  return `${new Date().getFullYear()}-01-01`;
}

/** Short, human-scannable form of a raw job id. */
function shortJobId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/** Raw job id for the tooltip — never the visible label. */
function entryTitle(
  entry: JobProfitabilityReport["entries"][number],
): string | undefined {
  return typeof entry.job_id === "string" && entry.job_id
    ? entry.job_id
    : undefined;
}

function entryName(
  entry: JobProfitabilityReport["entries"][number],
  index: number,
): string {
  const id = entry.job_id;
  return id ? `Job ${shortJobId(id)}` : `Job ${index + 1}`;
}

function entryRevenue(
  entry: JobProfitabilityReport["entries"][number],
): number {
  return entry.revenue_cents;
}

function entryCosts(entry: JobProfitabilityReport["entries"][number]): number {
  return entry.costs_cents;
}

function entryProfit(entry: JobProfitabilityReport["entries"][number]): number {
  return entry.profit_cents;
}

function entryKey(
  entry: JobProfitabilityReport["entries"][number],
  index: number,
): string | number {
  const id = entry.job_id;
  return typeof id === "string" && id ? id : index;
}

export function JobProfitabilityPage() {
  const [start, setStart] = useState(janFirstIso);
  const [end, setEnd] = useState(todayIso);
  const [applied, setApplied] = useState({
    start: janFirstIso(),
    end: todayIso(),
  });
  const [report, setReport] = useState<JobProfitabilityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback((query: { start: string; end: string }) => {
    setLoading(true);
    setError(null);
    getJobProfitability({ start_date: query.start, end_date: query.end })
      .then(setReport)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({ start, end });
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Work profitability"
        description="Revenue, costs, and profit per job for a date range."
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports">All reports</Link>
          </Button>
        }
      />

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <form
        onSubmit={submit}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="jp-start" className="grid gap-1 text-xs font-medium">
          Start date
          <Input
            id="jp-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="jp-end" className="grid gap-1 text-xs font-medium">
          End date
          <Input
            id="jp-end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <Button type="submit" disabled={loading || !start || !end}>
          {loading ? "Running…" : "Run report"}
        </Button>
      </form>

      {loading && report === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : report ? (
        <div className="grid gap-4">
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="job-profitability-summary"
          >
            <div className="table-scroll">
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      Total revenue ({report.start_date} – {report.end_date})
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(report.total_revenue_cents)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Total costs</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(report.total_costs_cents)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-semibold">
                      Total profit
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {money(report.total_profit_cents)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="job-profitability-summary-stack"
          >
            <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
              <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                <span className="record-title">Totals</span>
                <span className="font-semibold tabular-nums">
                  {money(report.total_profit_cents)} profit
                </span>
              </div>
              <CardField label="Period">
                {report.start_date} – {report.end_date}
              </CardField>
              <CardField label="Total revenue">
                {money(report.total_revenue_cents)}
              </CardField>
              <CardField label="Total costs">
                {money(report.total_costs_cents)}
              </CardField>
              <CardField label="Total profit">
                {money(report.total_profit_cents)}
              </CardField>
            </article>
          </div>

          <section aria-label="Per job" className="grid gap-3">
            <h2 className="surface rounded-xl bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
              Per job
            </h2>
            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="job-profitability-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Costs</TableHead>
                      <TableHead className="text-right">Profit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.entries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          No jobs with activity in this period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.entries.map((entry, index) => (
                        <TableRow key={entryKey(entry, index)}>
                          <TableCell className="font-medium">
                            <span title={entryTitle(entry)}>
                              {entryName(entry, index)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entryRevenue(entry))}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entryCosts(entry))}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entryProfit(entry))}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="job-profitability-stack"
            >
              {report.entries.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No jobs with activity in this period.
                </p>
              ) : (
                report.entries.map((entry, index) => (
                  <article
                    key={entryKey(entry, index)}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <span className="record-title" title={entryTitle(entry)}>
                        {entryName(entry, index)}
                      </span>
                      <span className="tabular-nums">
                        {money(entryProfit(entry))} profit
                      </span>
                    </div>
                    <CardField label="Revenue">
                      {money(entryRevenue(entry))}
                    </CardField>
                    <CardField label="Costs">{money(entryCosts(entry))}</CardField>
                    <CardField label="Profit">
                      {money(entryProfit(entry))}
                    </CardField>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
